import { isEqual, uniqueId } from 'lodash/fp';
import { navigateToMigrationWorkbench } from 'app/routes';
import { doLoadProjects, doUpdateProjectEnv } from 'app/store/projects/actions.js';
import {
    selectMigrationForUid,
    selectSelectedMigration,
    selectSelectedMigrationSheets,
    selectSelectedSheet,
    selectSourceSheets,
    selectExpandedGroups,
    selectGroups
} from './selectors.js';
import {
    CLOSE_CREATE_DIALOG,
    CLOSE_MIGRATE_DIALOG,
    CREATE_GRAPH,
    CREATE_MIGRATION,
    CREATE_PROJECT,
    GET_MIGRATION_FILES,
    LIST_MIGRATIONS,
    MIGRATE_SHEETS,
    OPEN_CREATE_DIALOG,
    OPEN_MIGRATE_DIALOG,
    SELECT_MIGRATION,
    SELECT_SHEET,
    SET_METADATA,
    SET_MIGRATION_SHEETS,
    UPLOAD_FILE,
    TOGGLE_GROUP,
    EXPAND_ALL_GROUPS,
    COLLAPSE_ALL_GROUPS
} from './types.js';
import { calculateStats, processFiles, translateSheets } from './utils.js';

export const doUploadFile = (uid, file) => {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('dapMigrationUid', uid);

    return {
        type: UPLOAD_FILE,
        api: { endpoint: `dapMigrationUpload`, sync: true },
        params: { formData }
    };
};

export const doOpenCreateDialog = () => ({
    type: OPEN_CREATE_DIALOG
});

export const doCloseCreateDialog = () => ({
    type: CLOSE_CREATE_DIALOG
});

export const doOpenMigrateDialog = () => ({
    type: OPEN_MIGRATE_DIALOG
});

export const doCloseMigrateDialog = () => ({
    type: CLOSE_MIGRATE_DIALOG
});

export const doListMigrations = () => ({
    type: LIST_MIGRATIONS,
    api: { version: 2, name: 'ListDapMigrations' }
});

export const doSelectMigration = (uid) => ({
    type: SELECT_MIGRATION,
    payload: { uid }
});

export const doToggleGroup = (id) => ({
    type: TOGGLE_GROUP,
    payload: { id }
});

export const doSelectSheet = (id, workbookId) => ({
    type: SELECT_SHEET,
    payload: { id, workbookId },
    effect: ({ store }) => {
        const state = store.getState();
        const expandedGroups = selectExpandedGroups(state);
        if (!expandedGroups.includes(workbookId)) {
            store.dispatch(doToggleGroup(workbookId));
        }
    }
});

export const doCreateMigration = ({ name }) => ({
    type: CREATE_MIGRATION,
    api: { version: 2, name: 'CreateDapMigration' },
    params: { name },
    onSucceeded: ({ payload, dispatch }) => {
        dispatch(doListMigrations());
        navigateToMigrationWorkbench(payload.uid);
    }
});

const doSetMigrationSheets = (uid, sheets) => ({
    type: SET_MIGRATION_SHEETS,
    payload: {
        uid,
        sheets
    }
});

const doSetMetadata = (uid, feMetadata) => ({
    type: SET_METADATA,
    api: { version: 2, name: 'UpdateDapMigration' },
    params: { uid, feMetadata }
});

export const doGetMigrationFiles = (uid) => {
    const files = [];

    return {
        type: GET_MIGRATION_FILES,
        api: { version: 2, name: 'StartFetchDapMigrationFiles' },
        params: { uid },
        onProgress: ({ payload }) => {
            files.push(payload);
        },
        onSucceeded: ({ getState, dispatch }) => {
            const state = getState();
            const sheets = processFiles(files);
            const stats = calculateStats(files);
            const migration = selectMigrationForUid(state, { uid });
            const feMetadata = migration?.feMetadata ?? {};

            dispatch(doSetMigrationSheets(uid, sheets));

            if (!isEqual(feMetadata.stats, stats)) {
                dispatch(doSetMetadata(uid, { ...feMetadata, stats }));
            }
        }
    };
};

const doCreateGraph = ({ projectUid, nodeActions, dependencies }) => ({
    type: CREATE_GRAPH,
    projectUid,
    nodeActions,
    dependencies,
    onSucceeded: ({ dispatch, getState, completedActions }) => {
        const state = getState();
        const sheets = selectSelectedMigrationSheets(state);
        const migration = selectSelectedMigration(state);
        const feMetadata = migration?.feMetadata ?? {};

        const idMappings = [];

        Object.values(completedActions).forEach(
            ({ id, spaceUid, viewUid, spaceName, spaceSlug, viewName, viewType }) => {
                const sheet = sheets.find((s) => s.id === id);

                idMappings.push({
                    sheetId: sheet?.sheetId,
                    workbookId: sheet?.workbookId,
                    spaceUid,
                    viewUid,
                    spaceName,
                    spaceSlug,
                    viewName,
                    viewType
                });
            }
        );

        dispatch(
            doSetMetadata(migration.uid, {
                ...feMetadata,
                idMappings: idMappings.concat(feMetadata.idMappings ?? [])
            })
        );
    }
});

const doCreateProject = ({ name, nodeActions, dependencies, envUid }) => ({
    type: CREATE_PROJECT,
    api: { version: 2, name: 'CreateSpaceWithView' },
    params: { name, envUid },
    onSucceeded: ({ payload, dispatch }) => {
        const projectUid = payload.entityId;

        dispatch(doCreateGraph({ projectUid, nodeActions, dependencies }));
        dispatch(doLoadProjects());
    }
});

export const doMigrateSheets = ({ newProjectName, projectUid, envUid, targetTable }) => ({
    type: MIGRATE_SHEETS,
    payload: { projectUid },
    effect: ({ store }) => {
        const state = store.getState();
        const selectedSheet = selectSelectedSheet(state);
        const sourceSheets = selectSourceSheets(state);
        const modelId = uniqueId('model');
        const sheets = [
            selectedSheet,
            ...sourceSheets,
            {
                id: modelId,
                targetTable,
                type: 'Model',
                sources: [selectedSheet.id],
                name: `${selectedSheet.name}_model`
            }
        ];

        const filteredSheets = sheets
            .filter(({ type }) => type !== 'ImportJob' && type !== 'DataLink')
            .map((sheet) => (sheet.type === 'DataSourceSheet' ? { ...sheet, sources: [] } : sheet));
        const nodeActions = translateSheets(filteredSheets);
        const dependencies = filteredSheets.reduce((dependencies, sheet) => {
            dependencies[sheet.id] = sheet.sources;

            return dependencies;
        }, {});

        if (newProjectName) {
            store.dispatch(
                doCreateProject({
                    name: newProjectName,
                    envUid,
                    nodeActions,
                    dependencies
                })
            );
        } else {
            store.dispatch(doUpdateProjectEnv(envUid, projectUid));
            store.dispatch(doCreateGraph({ projectUid, nodeActions, dependencies }));
        }
    }
});

export const doExpandAllGroups = () => ({
    type: EXPAND_ALL_GROUPS,
    payload: (state) => {
        const groups = selectGroups(state);
        return { ids: groups.map(({ id }) => id) };
    }
});

export const doCollapseAllGroups = () => ({
    type: COLLAPSE_ALL_GROUPS
});
